// ─────────────────────────────────────────────────────────────────────────────
// IT Services / Web Projects / Marketing & Advertising — API Client
//
// Follows the same pattern as frontend/src/features/work/api/projects.ts
// Uses the shared axios client from @/shared/api/axios
// ─────────────────────────────────────────────────────────────────────────────

import { api } from '@/shared/api/axios';

export const servicesProjectsApi = {
  // ── Rate Cards ──────────────────────────────────────────────────────────────
  async listRateCards(filters: { status?: string } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    const { data } = await api.get<{ rateCards: any[] }>(`/api/v2/services-projects/rate-cards?${params}`);
    return data.rateCards ?? [];
  },

  async getRateCard(id: string): Promise<any> {
    const { data } = await api.get<{ rateCard: any }>(`/api/v2/services-projects/rate-cards/${id}`);
    return data.rateCard;
  },

  async getActiveRateCard(): Promise<any | null> {
    const { data } = await api.get<{ rateCard: any }>('/api/v2/services-projects/rate-cards/active');
    return data.rateCard;
  },

  async createRateCard(payload: any): Promise<any> {
    const { data } = await api.post<{ rateCard: any }>('/api/v2/services-projects/rate-cards', payload);
    return data.rateCard;
  },

  async updateRateCard(id: string, payload: any): Promise<any> {
    const { data } = await api.put<{ rateCard: any }>(`/api/v2/services-projects/rate-cards/${id}`, payload);
    return data.rateCard;
  },

  async activateRateCard(id: string): Promise<any> {
    const { data } = await api.post<{ rateCard: any }>(`/api/v2/services-projects/rate-cards/${id}/activate`);
    return data.rateCard;
  },

  // ── Proposals ───────────────────────────────────────────────────────────────
  async listProposals(filters: { status?: string; clientId?: string; dealId?: string } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.clientId) params.set('clientId', filters.clientId);
    if (filters.dealId) params.set('dealId', filters.dealId);
    const { data } = await api.get<{ proposals: any[] }>(`/api/v2/services-projects/proposals?${params}`);
    return data.proposals ?? [];
  },

  async getProposal(id: string): Promise<any> {
    const { data } = await api.get<{ proposal: any }>(`/api/v2/services-projects/proposals/${id}`);
    return data.proposal;
  },

  async createProposal(payload: any): Promise<any> {
    const { data } = await api.post<{ proposal: any }>('/api/v2/services-projects/proposals', payload);
    return data.proposal;
  },

  async updateProposal(id: string, payload: any): Promise<any> {
    const { data } = await api.put<{ proposal: any }>(`/api/v2/services-projects/proposals/${id}`, payload);
    return data.proposal;
  },

  async sendProposal(id: string): Promise<any> {
    const { data } = await api.post<{ proposal: any }>(`/api/v2/services-projects/proposals/${id}/send`);
    return data.proposal;
  },

  async acceptProposal(id: string): Promise<any> {
    const { data } = await api.post<{ proposal: any }>(`/api/v2/services-projects/proposals/${id}/accept`);
    return data.proposal;
  },

  async rejectProposal(id: string, reason: string): Promise<any> {
    const { data } = await api.post<{ proposal: any }>(`/api/v2/services-projects/proposals/${id}/reject`, { reason });
    return data.proposal;
  },

  async convertProposalToContract(id: string, payload: any): Promise<{ contract: any; project: any }> {
    const { data } = await api.post<{ contract: any; project: any }>(`/api/v2/services-projects/proposals/${id}/convert-contract`, payload);
    return data;
  },

  // ── Contracts ───────────────────────────────────────────────────────────────
  async listContracts(filters: { status?: string; clientId?: string; proposalId?: string } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.clientId) params.set('clientId', filters.clientId);
    if (filters.proposalId) params.set('proposalId', filters.proposalId);
    const { data } = await api.get<{ contracts: any[] }>(`/api/v2/services-projects/contracts?${params}`);
    return data.contracts ?? [];
  },

  async getContract(id: string): Promise<any> {
    const { data } = await api.get<{ contract: any }>(`/api/v2/services-projects/contracts/${id}`);
    return data.contract;
  },

  async updateContract(id: string, payload: any): Promise<any> {
    const { data } = await api.put<{ contract: any }>(`/api/v2/services-projects/contracts/${id}`, payload);
    return data.contract;
  },

  async activateContract(id: string): Promise<any> {
    const { data } = await api.post<{ contract: any }>(`/api/v2/services-projects/contracts/${id}/activate`);
    return data.contract;
  },

  // ── Projects ────────────────────────────────────────────────────────────────
  async listProjects(filters: { status?: string; contractId?: string } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.contractId) params.set('contractId', filters.contractId);
    const { data } = await api.get<{ projects: any[] }>(`/api/v2/services-projects/projects?${params}`);
    return data.projects ?? [];
  },

  async getProject(id: string): Promise<any> {
    const { data } = await api.get<{ project: any }>(`/api/v2/services-projects/projects/${id}`);
    return data.project;
  },

  async createProject(payload: any): Promise<any> {
    const { data } = await api.post<{ project: any }>('/api/v2/services-projects/projects', payload);
    return data.project;
  },

  async updateProject(id: string, payload: any): Promise<any> {
    const { data } = await api.put<{ project: any }>(`/api/v2/services-projects/projects/${id}`, payload);
    return data.project;
  },

  async deleteProject(id: string): Promise<any> {
    const { data } = await api.delete<{ ok: boolean }>(`/api/v2/services-projects/projects/${id}`);
    return data;
  },

  // ── Campaigns ──────────────────────────────────────────────────────────────
  async listCampaigns(filters: { status?: string; contractId?: string; projectId?: string } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.contractId) params.set('contractId', filters.contractId);
    if (filters.projectId) params.set('projectId', filters.projectId);
    const { data } = await api.get<{ campaigns: any[] }>(`/api/v2/services-projects/campaigns?${params}`);
    return data.campaigns ?? [];
  },

  async getCampaign(id: string): Promise<any> {
    const { data } = await api.get<{ campaign: any }>(`/api/v2/services-projects/campaigns/${id}`);
    return data.campaign;
  },

  async createCampaign(payload: any): Promise<any> {
    const { data } = await api.post<{ campaign: any }>('/api/v2/services-projects/campaigns', payload);
    return data.campaign;
  },

  async updateCampaign(id: string, payload: any): Promise<any> {
    const { data } = await api.put<{ campaign: any }>(`/api/v2/services-projects/campaigns/${id}`, payload);
    return data.campaign;
  },

  async deleteCampaign(id: string): Promise<any> {
    const { data } = await api.delete<{ ok: boolean }>(`/api/v2/services-projects/campaigns/${id}`);
    return data;
  },

  // ── Timesheets ─────────────────────────────────────────────────────────────
  async listTimesheets(filters: { projectId?: string; userId?: string; status?: string; dateFrom?: string; dateTo?: string } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters.projectId) params.set('projectId', filters.projectId);
    if (filters.userId) params.set('userId', filters.userId);
    if (filters.status) params.set('status', filters.status);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    const { data } = await api.get<{ timesheets: any[] }>(`/api/v2/services-projects/timesheets?${params}`);
    return data.timesheets ?? [];
  },

  async getTimesheet(id: string): Promise<any> {
    const { data } = await api.get<{ timesheet: any }>(`/api/v2/services-projects/timesheets/${id}`);
    return data.timesheet;
  },

  async createTimesheet(payload: any): Promise<any> {
    const { data } = await api.post<{ timesheet: any }>('/api/v2/services-projects/timesheets', payload);
    return data.timesheet;
  },

  async updateTimesheet(id: string, payload: any): Promise<any> {
    const { data } = await api.put<{ timesheet: any }>(`/api/v2/services-projects/timesheets/${id}`, payload);
    return data.timesheet;
  },

  async deleteTimesheet(id: string): Promise<any> {
    const { data } = await api.delete<{ ok: boolean }>(`/api/v2/services-projects/timesheets/${id}`);
    return data;
  },

  // ── Billing / Invoices ─────────────────────────────────────────────────────
  async listInvoices(filters: { status?: string; projectId?: string; clientId?: string; billingType?: string } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.projectId) params.set('projectId', filters.projectId);
    if (filters.clientId) params.set('clientId', filters.clientId);
    if (filters.billingType) params.set('billingType', filters.billingType);
    const { data } = await api.get<{ invoices: any[] }>(`/api/v2/services-projects/billing?${params}`);
    return data.invoices ?? [];
  },

  async getInvoice(id: string): Promise<any> {
    const { data } = await api.get<{ invoice: any }>(`/api/v2/services-projects/billing/${id}`);
    return data.invoice;
  },

  async createInvoice(payload: any): Promise<any> {
    const { data } = await api.post<{ invoice: any }>('/api/v2/services-projects/billing', payload);
    return data.invoice;
  },

  async updateInvoice(id: string, payload: any): Promise<any> {
    const { data } = await api.put<{ invoice: any }>(`/api/v2/services-projects/billing/${id}`, payload);
    return data.invoice;
  },

  async deleteInvoice(id: string): Promise<any> {
    const { data } = await api.delete<{ ok: boolean }>(`/api/v2/services-projects/billing/${id}`);
    return data;
  },

  async sendInvoice(id: string): Promise<any> {
    const { data } = await api.post<{ invoice: any }>(`/api/v2/services-projects/billing/${id}/send`);
    return data.invoice;
  },

  async markInvoicePaid(id: string, payload: { amountMinor?: number; paymentDate?: string; paymentMethod?: string; reference?: string } = {}): Promise<any> {
    const { data } = await api.post<{ invoice: any }>(`/api/v2/services-projects/billing/${id}/mark_paid`, payload);
    return data.invoice;
  },

  async generateInvoice(payload: { projectId: string; contractId?: string; billingType?: string; autoPopulate?: boolean }): Promise<any> {
    const { data } = await api.post<{ invoice: any }>('/api/v2/services-projects/billing/generate', payload);
    return data.invoice;
  },

  // ── Revenue Recognition ────────────────────────────────────────────────────
  async listRevenueRecognition(filters: { status?: string; contractId?: string; projectId?: string; method?: string } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.contractId) params.set('contractId', filters.contractId);
    if (filters.projectId) params.set('projectId', filters.projectId);
    if (filters.method) params.set('method', filters.method);
    const { data } = await api.get<{ recognitions: any[] }>(`/api/v2/services-projects/revenue-recognition?${params}`);
    return data.recognitions ?? [];
  },

  async getRevenueRecognition(id: string): Promise<any> {
    const { data } = await api.get<{ recognition: any }>(`/api/v2/services-projects/revenue-recognition/${id}`);
    return data.recognition;
  },

  async getRevenueRecognitionByContract(contractId: string): Promise<any> {
    const { data } = await api.get<{ recognition: any }>(`/api/v2/services-projects/revenue-recognition/contract/${contractId}`);
    return data.recognition;
  },

  async createRevenueRecognition(payload: any): Promise<any> {
    const { data } = await api.post<{ recognition: any }>('/api/v2/services-projects/revenue-recognition', payload);
    return data.recognition;
  },

  async updateRevenueRecognition(id: string, payload: any): Promise<any> {
    const { data } = await api.put<{ recognition: any }>(`/api/v2/services-projects/revenue-recognition/${id}`, payload);
    return data.recognition;
  },

  async runRevenueRecognition(id: string): Promise<any> {
    const { data } = await api.post<{ recognition: any }>(`/api/v2/services-projects/revenue-recognition/${id}/run`);
    return data.recognition;
  },

  async recognizeRevenueItem(id: string, itemId: string): Promise<any> {
    const { data } = await api.post<{ recognition: any }>(`/api/v2/services-projects/revenue-recognition/${id}/recognize/${itemId}`);
    return data.recognition;
  },

  // ── Profitability ──────────────────────────────────────────────────────────
  async listProfitability(filters: { projectId?: string; period?: string; periodType?: string } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters.projectId) params.set('projectId', filters.projectId);
    if (filters.period) params.set('period', filters.period);
    if (filters.periodType) params.set('periodType', filters.periodType);
    const { data } = await api.get<{ profitability: any[] }>(`/api/v2/services-projects/profitability?${params}`);
    return data.profitability ?? [];
  },

  async getProfitability(id: string): Promise<any> {
    const { data } = await api.get<{ profitability: any }>(`/api/v2/services-projects/profitability/${id}`);
    return data.profitability;
  },

  async createProfitability(payload: any): Promise<any> {
    const { data } = await api.post<{ profitability: any }>('/api/v2/services-projects/profitability', payload);
    return data.profitability;
  },

  async updateProfitability(id: string, payload: any): Promise<any> {
    const { data } = await api.put<{ profitability: any }>(`/api/v2/services-projects/profitability/${id}`, payload);
    return data.profitability;
  },

  async computeProfitability(projectId: string, payload: any): Promise<any> {
    const { data } = await api.post<{ profitability: any }>(`/api/v2/services-projects/profitability/compute/${projectId}`, payload);
    return data.profitability;
  },

  // ── Expense Allocations ────────────────────────────────────────────────────
  async listExpenseAllocations(filters: { projectId?: string; category?: string; status?: string } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters.projectId) params.set('projectId', filters.projectId);
    if (filters.category) params.set('category', filters.category);
    if (filters.status) params.set('status', filters.status);
    const { data } = await api.get<{ expenses: any[] }>(`/api/v2/services-projects/expense-allocations?${params}`);
    return data.expenses ?? [];
  },

  async createExpenseAllocation(payload: any): Promise<any> {
    const { data } = await api.post<{ expense: any }>('/api/v2/services-projects/expense-allocations', payload);
    return data.expense;
  },

  async updateExpenseAllocation(id: string, payload: any): Promise<any> {
    const { data } = await api.put<{ expense: any }>(`/api/v2/services-projects/expense-allocations/${id}`, payload);
    return data.expense;
  },

  // ── Health ──────────────────────────────────────────────────────────────────
  async health(): Promise<{ ok: boolean }> {
    const { data } = await api.get<{ ok: boolean }>('/api/v2/services-projects/health');
    return data;
  },
};

// Query keys factory (following work/api/queryKeys.ts pattern)
export const servicesProjectsQueryKeys = {
  root: ['services-projects'] as const,
  rateCards: () => [...servicesProjectsQueryKeys.root, 'rate-cards'] as const,
  rateCardsList: (filters: { status?: string } = {}) => [...servicesProjectsQueryKeys.rateCards(), 'list', filters] as const,
  rateCard: (id: string) => [...servicesProjectsQueryKeys.rateCards(), 'detail', id] as const,
  activeRateCard: () => [...servicesProjectsQueryKeys.rateCards(), 'active'] as const,

  proposals: () => [...servicesProjectsQueryKeys.root, 'proposals'] as const,
  proposalsList: (filters: { status?: string; clientId?: string; dealId?: string } = {}) =>
    [...servicesProjectsQueryKeys.proposals(), 'list', filters] as const,
  proposal: (id: string) => [...servicesProjectsQueryKeys.proposals(), 'detail', id] as const,

  contracts: () => [...servicesProjectsQueryKeys.root, 'contracts'] as const,
  contractsList: (filters: { status?: string; clientId?: string; proposalId?: string } = {}) =>
    [...servicesProjectsQueryKeys.contracts(), 'list', filters] as const,
  contract: (id: string) => [...servicesProjectsQueryKeys.contracts(), 'detail', id] as const,

  projects: () => [...servicesProjectsQueryKeys.root, 'projects'] as const,
  projectsList: (filters: { status?: string; contractId?: string } = {}) =>
    [...servicesProjectsQueryKeys.projects(), 'list', filters] as const,
  project: (id: string) => [...servicesProjectsQueryKeys.projects(), 'detail', id] as const,

  campaigns: () => [...servicesProjectsQueryKeys.root, 'campaigns'] as const,
  campaignsList: (filters: { status?: string; contractId?: string; projectId?: string } = {}) =>
    [...servicesProjectsQueryKeys.campaigns(), 'list', filters] as const,
  campaign: (id: string) => [...servicesProjectsQueryKeys.campaigns(), 'detail', id] as const,

  timesheets: () => [...servicesProjectsQueryKeys.root, 'timesheets'] as const,
  timesheetsList: (filters: { projectId?: string; userId?: string; status?: string; dateFrom?: string; dateTo?: string } = {}) =>
    [...servicesProjectsQueryKeys.timesheets(), 'list', filters] as const,

  billing: () => [...servicesProjectsQueryKeys.root, 'billing'] as const,
  invoicesList: (filters: { status?: string; projectId?: string; clientId?: string; billingType?: string } = {}) =>
    [...servicesProjectsQueryKeys.billing(), 'list', filters] as const,
  invoice: (id: string) => [...servicesProjectsQueryKeys.billing(), 'detail', id] as const,

  revenueRecognition: () => [...servicesProjectsQueryKeys.root, 'revenue-recognition'] as const,
  revenueRecognitionList: (filters: { status?: string; contractId?: string; projectId?: string; method?: string } = {}) =>
    [...servicesProjectsQueryKeys.revenueRecognition(), 'list', filters] as const,
  revenueRecognitionDetail: (id: string) => [...servicesProjectsQueryKeys.revenueRecognition(), 'detail', id] as const,

  profitability: () => [...servicesProjectsQueryKeys.root, 'profitability'] as const,
  profitabilityList: (filters: { projectId?: string; period?: string; periodType?: string } = {}) =>
    [...servicesProjectsQueryKeys.profitability(), 'list', filters] as const,
  profitabilityDetail: (id: string) => [...servicesProjectsQueryKeys.profitability(), 'detail', id] as const,

  expenseAllocations: () => [...servicesProjectsQueryKeys.root, 'expense-allocations'] as const,
  expenseAllocationsList: (filters: { projectId?: string; category?: string; status?: string } = {}) =>
    [...servicesProjectsQueryKeys.expenseAllocations(), 'list', filters] as const,
  expenseAllocationDetail: (id: string) => [...servicesProjectsQueryKeys.expenseAllocations(), 'detail', id] as const,
};